import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import { validateDiscountCodeCall, recordDiscountCodeUsageCall, cancelSubscriptionImmediatelyCall, clearNextProductCall } from "../../api/apiOperations";
import { subscribeToProduct } from "../../api/apiEndpoints";
import { setAppliedDiscount, clearAppliedDiscount, setDiscountError, setSubscriptionId, setClientSecretStripe, clearClientSecretStripe } from "../../slices/appSlice";
import {
  Container,
  InputRow,
  DiscountInput,
  ApplyButton,
  DiscountMessage,
  AppliedDiscount,
  RemoveButton,
} from "./styles";
import { getAuth } from "firebase/auth";

type RootState = {
  appData: {
    appliedDiscount: {
      code: string;
      type: string;
      amount: number;
      description?: string;
      stripePromoId?: string | null;
    } | null;
    subscriptionId: string | null;
    selectedProduct?: {
      businessProductId: string;
    };
    discountError: string | null;
  };
};

const DiscountCodeInput: React.FC = () => {
  const [discountCode, setDiscountCode] = useState<string>("");
  const [isApplying, setIsApplying] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const previousDiscountRef = useRef<any>(null);
  const isProcessingRef = useRef<boolean>(false); // Prevent duplicate calls
  const lastClickTimeRef = useRef<number>(0);
  const dispatch = useDispatch();

  const { appliedDiscount, subscriptionId, selectedProduct, discountError } = useSelector((state: RootState) => state.appData);

  // Sync Redux discountError to local state so it survives Elements remounts
  useEffect(() => {
    if (discountError) {
      setErrorMessage(discountError);
    }
  }, [discountError]);

  // Store current discount for potential restoration
  useEffect(() => {
    if (appliedDiscount) {
      previousDiscountRef.current = appliedDiscount;
    }
  }, [appliedDiscount]);

  const handleApplyDiscountWithDebounce = (e: React.MouseEvent) => {
    e.preventDefault();
    
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    // Prevent clicks within 2 seconds of each other
    if (timeSinceLastClick < 2000) {
      // console.log("🚫 [DEBOUNCE] Click ignored, too soon after last click:", timeSinceLastClick, "ms");
      return;
    }
    
    lastClickTimeRef.current = now;
    // console.log("✅ [DEBOUNCE] Click accepted, calling handleApplyDiscount");
    handleApplyDiscount();
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) return;

    setIsApplying(true);
    setErrorMessage("");
    
    // Dispatch event to show loading state
    window.dispatchEvent(new CustomEvent('discountProcessingStart'));

    try {
      // 1. Validate the discount code
      const result = await validateDiscountCodeCall(discountCode.trim(), subscriptionId ?? undefined, selectedProduct?.businessProductId);

      if (result.valid) {
        // 2. Cancel old subscription if it exists
        if (subscriptionId) {
          await cancelSubscriptionImmediatelyCall(subscriptionId);
          dispatch(setSubscriptionId(''));
        }

        // 3. Create new subscription with promo
        if (!selectedProduct?.businessProductId) {
          throw new Error("No selected product found while applying discount.");
        }

        // Send the raw code (not the promo id) so the backend resolves it authoritatively.
        // Previously used result.stripePromoId! which was null for trial codes / codes
        // whose promo id was missing, silently creating a full-price sub while the UI
        // showed a discount applied.
        const response = await subscribeToProduct(
          selectedProduct.businessProductId,
          result.code
        );

        if (response?.stripeSubscriptionId && response?.clientSecret) {
          dispatch(setSubscriptionId(response.stripeSubscriptionId));
          dispatch(setAppliedDiscount({
            code: result.code,
            type: result.type,
            amount: result.amount,
            description: result.description,
            stripePromoId: result.stripePromoId,
          }));
          dispatch(setClientSecretStripe(response.clientSecret));
          setDiscountCode("");
        } else {
          setErrorMessage("Could not create a new subscription. Please try again.");
          window.dispatchEvent(new CustomEvent('discountProcessingComplete'));
        }
      } else {
        setErrorMessage(result.message || "Invalid discount code");
        dispatch(setDiscountError(result.message || "Invalid discount code"));
        window.dispatchEvent(new CustomEvent('discountProcessingComplete'));
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setErrorMessage("Invalid discount code");
      dispatch(setDiscountError("Invalid discount code"));
      window.dispatchEvent(new CustomEvent('discountProcessingComplete'));
    } finally {
      setIsApplying(false);
    }
  };

  // Record discount usage after subscription is created
  useEffect(() => {
    if (appliedDiscount && subscriptionId) {
      const user = getAuth().currentUser;
      const userEmail = user?.email;
  
      if (!userEmail) {
        // console.warn("No userEmail found. Skipping discount usage recording.");
        return;
      }
  
      // const recordUsage = async () => {
      //   try {
      //     console.log("📝 Recording discount usage:", {
      //       code: appliedDiscount.code,
      //       subscriptionId,
      //       userEmail
      //     });
          
      //     const result = await recordDiscountCodeUsageCall(appliedDiscount.code, subscriptionId, userEmail);
      //     if (result.success) {
      //       console.log("✅ Successfully recorded discount code usage");
      //     } else {
      //       console.error("❌ Failed to record discount code usage: API returned success: false");
      //     }
      //   } catch (error) {
      //     console.error("❌ Failed to record discount code usage:", error);
      //   }
      // };
  
      // recordUsage();
    }
  }, [appliedDiscount, subscriptionId]);

  const handleRemoveDiscount = async () => {
    // CRITICAL: Prevent duplicate calls
    if (isProcessingRef.current) {
      // console.log("🚫 Remove discount already in progress, ignoring duplicate call");
      return;
    }
    
    isProcessingRef.current = true;
    
    // console.log("🗑️ [REMOVE] Starting discount removal process");
    // console.log("🐛 [REMOVE] Current state:", {
    //   appliedDiscount,
    //   subscriptionId,
    //   hasSelectedProduct: !!selectedProduct
    // });
    
    // Dispatch event to show loading state
    window.dispatchEvent(new CustomEvent('discountProcessingStart'));
    
    try {
      // STEP 1: Clear ALL cached data and discount state
      // console.log("🧹 [REMOVE] Step 1: Clearing cached data");
      await clearNextProductCall();
      
      // STEP 2: Cancel current subscription if it exists
      if (subscriptionId) {
        // console.log("🗑️ [REMOVE] Step 2: Canceling subscription:", subscriptionId);
        const cancelResult = await cancelSubscriptionImmediatelyCall(subscriptionId);
        // console.log("📋 [REMOVE] Cancel result:", cancelResult);
        
        if (!cancelResult.success) {
          throw new Error("Failed to cancel existing subscription");
        }
        
        // Clear subscription state and client secret
        dispatch(setSubscriptionId(''));
        dispatch(clearClientSecretStripe());
        dispatch(clearAppliedDiscount());
        
        // Longer delay to ensure cleanup
        // console.log("⏳ [REMOVE] Waiting for cleanup...");
        await new Promise(resolve => setTimeout(resolve, 1500));
      }
      
      // STEP 3: Clear cached data again to be extra sure
      // console.log("🧹 [REMOVE] Step 3: Final cache clear");
      await clearNextProductCall();
      
      // STEP 4: Create SINGLE new subscription without discount
      if (selectedProduct?.businessProductId) {
        // console.log("🔄 [REMOVE] Step 4: Creating SINGLE subscription without discount");
        // console.log("📦 [REMOVE] API call params:", {
        //   businessProductId: selectedProduct.businessProductId,
        //   promoCode: undefined
        // });
        
        const response = await subscribeToProduct(
          selectedProduct.businessProductId,
          undefined
        );
        
        // console.log("📦 [REMOVE] New subscription response:", {
        //   hasSubscriptionId: !!response?.stripeSubscriptionId,
        //   hasClientSecret: !!response?.clientSecret,
        //   subscriptionId: response?.stripeSubscriptionId,
        //   clientSecretPrefix: response?.clientSecret?.substring(0, 10)
        // });
        
        if (response?.stripeSubscriptionId && response?.clientSecret) {
          // Update subscription ID
          // console.log("✅ [REMOVE] Step 5: NEW subscription created:", response.stripeSubscriptionId);
          dispatch(setSubscriptionId(response.stripeSubscriptionId));
          
          // Update client secret with longer delay
          setTimeout(() => {
            // console.log("🔑 [REMOVE] Step 6: Setting client secret");
            dispatch(setClientSecretStripe(response.clientSecret));
            // console.log("✅ [REMOVE] Discount removal complete!");
          }, 500);
        } else {
          throw new Error(`Failed to create new subscription - Response: ${JSON.stringify(response)}`);
        }
      } else {
        throw new Error("No selected product found");
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      // console.error("❌ [REMOVE] Error during removal:", error);
      
      // Restore discount state if removal failed
      if (previousDiscountRef.current) {
        // console.log("🔄 [REMOVE] Restoring discount state due to failure");
        dispatch(setAppliedDiscount(previousDiscountRef.current));
      }
      
      setErrorMessage(`Failed to remove discount: ${errorMessage}. Please try again or refresh the page.`);
      
      // Dispatch event to hide loading state on error
      window.dispatchEvent(new CustomEvent('discountProcessingComplete'));
    } finally {
      isProcessingRef.current = false; // Reset the processing flag
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApplyDiscount();
    }
  };

  const getDiscountText = () => {
    if (!appliedDiscount) return "";

    return appliedDiscount.type === "percentage"
      ? `${appliedDiscount.amount}% off`
      : `$${appliedDiscount.amount.toFixed(2)} off`;
  };

  return (
    <Container>
      {appliedDiscount ? (
        <AppliedDiscount>
          <div>
            <strong>Discount applied:</strong> {appliedDiscount.code} ({getDiscountText()})
            {appliedDiscount.description && ` - ${appliedDiscount.description}`}
          </div>
          <RemoveButton onClick={handleRemoveDiscount} disabled={isProcessingRef.current}>Remove</RemoveButton>
        </AppliedDiscount>
      ) : (
        <>
          <InputRow>
            <DiscountInput
              placeholder="Discount Code"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isApplying}
            />
            <ApplyButton
              onClick={handleApplyDiscountWithDebounce}
              disabled={!discountCode.trim() || isApplying || isProcessingRef.current}
            >
              {isApplying ? <CircularProgress size={20} color="inherit" /> : "Apply"}
            </ApplyButton>
          </InputRow>
          {errorMessage && <DiscountMessage isError>{errorMessage}</DiscountMessage>}
        </>
      )}
    </Container>
  );
};

export default DiscountCodeInput;